/**
 * Provisioning Progress Display
 *
 * Displays real-time step-by-step progress during router provisioning.
 * Shows which steps have completed, which are in progress, and which failed.
 * Allows expanding individual steps to see detailed results and error messages.
 */

import { useState } from 'react';
import {
    FaCheckCircle, FaTimesCircle, FaClock, FaSpinner, FaChevronDown, FaExclamationTriangle
} from 'react-icons/fa';
import './ProvisioningProgressDisplay.scss';

const ProvisioningProgressDisplay = ({ result, isProvisioning = false }) => {
    const [expandedStepId, setExpandedStepId] = useState(null);

    if (!result && !isProvisioning) {
        return null;
    }

    const getStepIcon = (step) => {
        if (step.success) {
            return <FaCheckCircle className="icon success" />;
        }
        if (isProvisioning && !step.success && !step.executedAt) {
            return <FaSpinner className="icon spinner" />;
        }
        return <FaTimesCircle className="icon error" />;
    };

    const getStepStatus = (step) => {
        if (step.success) return 'completed';
        if (isProvisioning && !step.executedAt) return 'in-progress';
        return 'failed';
    };

    const progressPercentage = result
        ? (result.completedStep / 11) * 100
        : 0;

    return (
        <div className="provisioning-progress-display">
            {/* Overall Status Banner */}
            <div className={`status-banner ${result?.success ? 'success' : result ? 'failed' : 'in-progress'}`}>
                <div className="status-icon">
                    {isProvisioning ? (
                        <FaSpinner className="spinner-icon" />
                    ) : result?.success ? (
                        <FaCheckCircle />
                    ) : (
                        <FaExclamationTriangle />
                    )}
                </div>
                <div className="status-content">
                    <h3>{isProvisioning ? 'Provisioning in progress...' : result?.success ? 'Provisioning complete!' : 'Provisioning failed'}</h3>
                    <p>{result?.message || 'Starting router provisioning...'}</p>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-container">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progressPercentage}%` }} />
                </div>
                <div className="progress-text">
                    Step {result?.completedStep || 0} of 11
                </div>
            </div>

            {/* Steps List */}
            <div className="steps-list">
                {result?.steps && result.steps.map((step, index) => (
                    <div key={index} className={`step-item ${getStepStatus(step)}`}>
                        <div className="step-header" onClick={() => setExpandedStepId(expandedStepId === index ? null : index)}>
                            <div className="step-number">{step.stepNumber}</div>
                            <div className="step-icon">{getStepIcon(step)}</div>
                            <div className="step-info">
                                <h4>{step.stepName}</h4>
                                {step.executedAt && (
                                    <span className="step-time">
                                        {new Date(step.executedAt).toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                            {step.details && (
                                <button className="expand-button">
                                    <FaChevronDown className={expandedStepId === index ? 'expanded' : ''} />
                                </button>
                            )}
                        </div>

                        {expandedStepId === index && step.details && (
                            <div className="step-details">
                                <p>{step.details}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Pending Steps Placeholder */}
            {isProvisioning && result?.steps && result.steps.length < 11 && (
                <div className="pending-steps">
                    {Array.from({ length: 11 - (result?.steps?.length || 0) }).map((_, i) => (
                        <div key={`pending-${i}`} className="step-item pending">
                            <div className="step-header">
                                <div className="step-number">{(result?.steps?.length || 0) + i + 1}</div>
                                <div className="step-icon">
                                    <FaClock className="icon pending" />
                                </div>
                                <div className="step-info">
                                    <h4>Waiting...</h4>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Error Summary */}
            {result && !result.success && result.steps?.some(s => !s.success) && (
                <div className="error-summary">
                    <h4>⚠️ Failed Steps</h4>
                    {result.steps
                        .filter(s => !s.success)
                        .map((step, i) => (
                            <div key={i} className="error-item">
                                <strong>{step.stepName}:</strong> {step.details}
                            </div>
                        ))}
                </div>
            )}

            {/* Action Buttons */}
            {result && !isProvisioning && (
                <div className="action-buttons">
                    {result.success && (
                        <div className="success-actions">
                            <p>✓ Router has been successfully provisioned and configured.</p>
                        </div>
                    )}
                    {!result.success && result.completedStep > 0 && (
                        <div className="partial-success">
                            <p>⚠️ Provisioning completed {result.completedStep} steps before failing.</p>
                            <p>Check the error details above and try provisioning again.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProvisioningProgressDisplay;
